import { useState, useEffect } from 'react';
import { Box, Grid, Typography, Button, Card, CardMedia, Stack, Avatar, Skeleton, IconButton } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CircleIcon from '@mui/icons-material/Circle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { FixcenterFilterDto } from '../../../components/product-filters-menu/models/fixcenter-filter-dto.model';
import { getFixcenterCategories } from '../../../services/products.service';
import { Category } from '../../../components/products-page/components/products-categories/models/Category.model';
import { useBetween } from 'use-between';
import { ProductImageUseBetween } from '../../../components/first-level-product-card/use-between/product-image-use-between';
import { getProductImageUrl } from '../../../shared/services/get-attachments.service';
import background from '../../../assets/img/fixcenter-background.png';
import { useTranslation } from '../../../i18n/useTranslation';
import { getCmsLinkToServices } from '../../../containers/cms-container/services/cms-navigation.service';
import { useNavigate } from 'react-router-dom';
import FixcenterSupport from '../../../components/fixcenter-support/FixcenterSupport';
import FixcenterFoooterBanner from '../../../components/fixcenter-footer-banner/FixcenterFooterBanner';
import * as authService from '../../../auth/services/auth.service';
import { Utils } from '../../../shared/constants/utilities.constants';

export default function ProductsPage() {
    const [index, setIndex] = useState(0);
    const [page, setPage] = useState(0);
    const [mainCategories, setMainCategories] = useState<Category[]>([]);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const ITEMS_PER_PAGE = 4;
    const { setHoverProductImageUuid } = useBetween(ProductImageUseBetween);
    const { t } = useTranslation('translation');
    const navigate = useNavigate();

    useEffect(() => {
        fetchCategories();
    }, []);

    const viewMore = (): void => {
        navigate(getCmsLinkToServices());
    };

    const goToCategory = (category: Category): void => {
        if (checkIfIsLoggedIn()) {
            navigate(`/webshop/category/${category.name}/${category.id}`);
        } else {
            navigate('/login');
        }
    };

    const checkIfIsLoggedIn = (): boolean => {
        const isLoggedIn: boolean = authService.checkLogInStatus();
        return isLoggedIn;
    };

    const fetchCategories = async () => {
        try {
            const languageCode = localStorage.getItem('lang');
            const fixcenterFilter: FixcenterFilterDto = {
                productFilter: {
                    isCategory: true,
                    parentProductId: Utils.defaultUuid,
                },
                languageCode: languageCode || undefined,
            };
            const response: any = await getFixcenterCategories(fixcenterFilter);
            if (response?.data) {
                const list = response.data as Category[];
                const filtered = list.filter((c) => c.imageIds?.some((id) => id && id !== Utils.defaultUuid));
                const finalList = filtered.length ? filtered : list;
                setMainCategories(finalList);
                if (finalList.length > 0) {
                    setHoverProductImageUuid(finalList[0].imageIds[0]);
                }
                setIsLoaded(true);
            }
        } catch (error) {
            console.error(error);
            setIsLoaded(true);
        }
    };

    const safeIndex = Math.min(Math.max(0, index), Math.max(0, mainCategories.length - 1));

    const current = mainCategories[safeIndex];
    const totalPages = Math.ceil(mainCategories.length / ITEMS_PER_PAGE);

    const paginatedItems = mainCategories.slice(page * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE + ITEMS_PER_PAGE);

    return (
        <Box
            sx={{
                backgroundImage: `url(${background})`,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    px: { xs: 2, md: 6 },
                    py: 6,
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <Grid container spacing={4} justifyContent="center" alignItems="center" sx={{ maxWidth: 1280 }}>
                    <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Stack spacing={1} alignItems="flex-start" sx={{ width: '100%', maxWidth: 300 }}>
                            <Typography variant="h4" sx={{ textAlign: 'left' }}>
                                {t('WELCOME_TO')}
                            </Typography>
                            <Typography variant="h5" color="#821818" sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                                FIXCENTER
                            </Typography>
                            <Typography variant="body1" sx={{ textAlign: 'left' }}>
                                {t('WE_SUPPORT_YOU')}
                            </Typography>
                            <Button
                                variant="contained"
                                color="error"
                                sx={{ mt: 2, backgroundColor: '#821818' }}
                                onClick={viewMore}
                            >
                                {t('VIEW_MORE')}
                            </Button>
                        </Stack>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                height: { xs: 260, md: 340 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                maxWidth: 360,
                            }}
                        >
                            {!isLoaded ? (
                                <Skeleton variant="rectangular" width={260} height={260} sx={{ borderRadius: 2 }} />
                            ) : current ? (
                                <motion.div
                                    key={current.id}
                                    initial={{ y: -40, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.45 }}
                                    style={{ width: '100%', maxWidth: 300 }}
                                >
                                    <Card
                                        elevation={3}
                                        sx={{
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            p: 2,
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <CardMedia
                                            component="img"
                                            src={getProductImageUrl(current.imageIds[0])}
                                            sx={{
                                                width: '100%',
                                                maxWidth: 260,
                                                maxHeight: 260,
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </Card>
                                    <Typography align="center" sx={{ mt: 1, fontWeight: 600 }}>
                                        {current.translation || current.name}
                                    </Typography>
                                </motion.div>
                            ) : null}
                        </Box>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={5}
                        sx={{ display: 'flex', justifyContent: 'center', position: 'relative', alignItems: 'center' }}
                    >
                        {!isLoaded ? (
                            <Grid container spacing={2}>
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <Grid
                                        key={i}
                                        item
                                        xs={12}
                                        sm={6}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Stack
                                            spacing={1}
                                            alignItems="center"
                                            sx={{
                                                flexShrink: 0,
                                                minHeight: 200,
                                            }}
                                        >
                                            <Skeleton
                                                variant="rounded"
                                                width={150}
                                                height={150}
                                                sx={{
                                                    borderRadius: 3,
                                                    flexShrink: 0,
                                                }}
                                            />

                                            <Skeleton
                                                variant="rectangular"
                                                width={120}
                                                height={20}
                                                sx={{
                                                    borderRadius: 1,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Box sx={{ width: '100%', maxWidth: 460, position: 'relative' }}>
                                <IconButton
                                    disabled={page === 0}
                                    onClick={() => setPage(page - 1)}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: -28,
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'white',
                                        boxShadow: 2,
                                    }}
                                >
                                    <ArrowBackIosNewIcon />
                                </IconButton>

                                <IconButton
                                    disabled={page === totalPages - 1}
                                    onClick={() => setPage(page + 1)}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: -28,
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'white',
                                        boxShadow: 2,
                                    }}
                                >
                                    <ArrowForwardIosIcon />
                                </IconButton>

                                <Grid container spacing={3} wrap="wrap" justifyContent="center">
                                    {paginatedItems.map((c) => {
                                        const imgId = c.imageIds?.find((id) => id && id !== Utils.defaultUuid);
                                        const isActive = mainCategories.indexOf(c) === safeIndex;
                                        return (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                key={c.id}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Stack
                                                    spacing={1}
                                                    alignItems="center"
                                                    onMouseEnter={() => setIndex(mainCategories.indexOf(c))}
                                                    onClick={() => {
                                                        goToCategory(c);
                                                    }}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        p: 1,
                                                        minWidth: 100,
                                                        maxWidth: 220,
                                                        borderRadius: 2,
                                                        bgcolor: isActive ? 'rgba(255,0,0,0.08)' : 'transparent',
                                                        transition: '0.2s',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 130,
                                                            height: 130,
                                                            borderRadius: '50%',
                                                            bgcolor: 'white',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: 1,
                                                        }}
                                                    >
                                                        <Avatar
                                                            src={imgId ? getProductImageUrl(imgId) : undefined}
                                                            sx={{
                                                                width: 105,
                                                                height: 105,
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                            textAlign: 'center',
                                                            whiteSpace: 'normal',
                                                            overflowWrap: 'break-word',
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {c.translation || c.name}
                                                    </Typography>
                                                </Stack>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                                    {Array.from({ length: totalPages }).map((_, i) => (
                                        <IconButton key={i} onClick={() => setPage(i)} size="small">
                                            {i === page ? (
                                                <CircleIcon fontSize="small" />
                                            ) : (
                                                <RadioButtonUncheckedIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ width: '100%', display: 'flex' }}>
                <FixcenterSupport variant={1} />
            </Box>
            <Box sx={{ width: '100%', display: 'flex' }}>
                <FixcenterFoooterBanner />
            </Box>
        </Box>
    );
}
